/** Фильтр с множественным выбором. */

import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  showCounts = false,
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (value: string[]) => void;
  showCounts?: boolean;
}) {
  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  };

  const selectedLabels = options
    .filter((option) => selected.includes(option.value))
    .map((option) => option.label);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={selected.length > 0 ? 'secondary' : 'outline'}
          size="sm"
          className="h-8 gap-1.5"
        >
          {label}
          {selected.length > 0 ? (
            <Badge variant="secondary" className="h-4 px-1 text-2xs tabular-nums">
              {selected.length}
            </Badge>
          ) : null}
          <ChevronDownIcon className="size-3.5 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          {options.length > 8 ? <CommandInput placeholder={`${label}…`} /> : null}
          <CommandList>
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const active = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                    className="gap-2"
                  >
                    <CheckIcon className={cn('size-4', active ? '' : 'invisible')} />
                    <span className="flex-1 truncate">{option.label}</span>
                    {showCounts && option.count !== undefined ? (
                      <span className="text-2xs text-muted-foreground tabular-nums">
                        {option.count}
                      </span>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.length > 0 ? (
          <div className="flex items-center justify-between gap-2 border-t px-2 py-1.5">
            <span
              className="truncate text-2xs text-muted-foreground"
              title={selectedLabels.join(', ')}
            >
              {selectedLabels.join(', ')}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 shrink-0 px-2 text-2xs"
              onClick={() => onChange([])}
            >
              Очистить
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
